/**
 * Kitomira : améliorations légères. Le site reste utilisable sans ce fichier (formulaires classiques en repli).
 * 1. Formulaires [data-kito-ajout] (« Complète le look ») et [data-kito-cadeau-form] : ajout sur place via Shopify.actions,
 *    le panier latéral se met à jour et s'ouvre.
 * 2. Case « Emballage cadeau » [data-kito-emballage] : ajoute ou retire l'emballage (quantité 1 au maximum).
 * 3. Cadeau offert : ajouté automatiquement quand le total des bijoux après remises atteint le palier (59 € par défaut),
 *    retiré en dessous, quantité ramenée à 1. Le cadeau et l'emballage ne comptent pas dans ce total.
 *    La gratuité elle-même vient de la remise automatique Shopify « Achetez X, obtenez Y ».
 * 4. Vidéos du héros [data-kito-video] : chargées après la page, seule la vidéo affichée ; poster ; lecture refusée → Ken Burns + bouton lecture ; jamais avec « réduire les animations ».
 * 5. Bijou qui tourne [data-kito-sequence] : séquence d'images dessinée dans un canvas selon le défilement, chargée à l'approche.
 * 6. Flèches des carrousels [data-kito-fleches] : précédent / suivant (le défilement au doigt marche sans JavaScript).
 * 7. Motion sobre : apparitions au défilement (une seule fois, opacité + translation) et respiration lumineuse continue, active seulement à l'écran.
 * 9. Bouton unique « pause des animations » (bandeau, lumière, Ken Burns, vidéos), mémorisé.
 * 8. Vidéo des fiches : lue muette en boucle quand elle est à l'écran, en pause sinon (réglage du bloc Médias).
 */

const reduireAnimations = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @returns {{seuilCadeau:number, cadeauActif:boolean, cadeauProduit:number, cadeauVariante:number, emballageProduit:number, emballageVariante:number}} */
function lireConfig() {
  try {
    return JSON.parse(document.getElementById('kito-config')?.textContent || '{}');
  } catch {
    return /** @type {any} */ ({});
  }
}
const CONFIG = lireConfig();

/** @param {Array<{id?:string, merchandiseId?:string, quantity:number}>} lines */
async function modifierPanier(lines) {
  const actions = window.Shopify?.actions;
  if (!actions?.updateCart) throw new Error('Shopify.actions indisponible');
  const resultat = await actions.updateCart({ lines }, { event: { context: 'cart' } });
  if (resultat?.userErrors?.length) throw new Error(resultat.userErrors[0].message);
  return resultat;
}

// ---------- 1. Ajouts sur place ----------
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-kito-ajout], [data-kito-cadeau-form]')) return;
  if (!window.Shopify?.actions?.updateCart) return; // envoi classique

  event.preventDefault();
  const bouton = form.querySelector('button[type="submit"]');
  const idVariante = new FormData(form).get('id');
  if (!idVariante) return;

  bouton?.setAttribute('aria-busy', 'true');
  if (bouton) bouton.disabled = true;
  try {
    await modifierPanier([{ merchandiseId: String(idVariante), quantity: 1 }]);
    if (form.matches('[data-kito-ajout]')) await window.Shopify.actions.openCart?.();
  } catch (erreur) {
    console.warn('[kitomira] ajout sur place impossible, envoi classique', erreur);
    form.submit();
  } finally {
    bouton?.removeAttribute('aria-busy');
    if (bouton) bouton.disabled = false;
  }
});

// ---------- 2. Emballage cadeau ----------
document.addEventListener('change', async (event) => {
  const caseACocher = event.target;
  if (!(caseACocher instanceof HTMLInputElement) || caseACocher.name !== 'kito_emballage') return;
  const form = caseACocher.closest('form[data-kito-emballage]');
  if (!(form instanceof HTMLFormElement)) return;
  if (!window.Shopify?.actions?.updateCart) {
    form.submit();
    return;
  }

  caseACocher.disabled = true;
  try {
    if (caseACocher.checked && form.dataset.etat === 'absent' && form.dataset.variante) {
      await modifierPanier([{ merchandiseId: form.dataset.variante, quantity: 1 }]);
    } else if (!caseACocher.checked && form.dataset.etat === 'present' && form.dataset.cle) {
      await modifierPanier([{ id: form.dataset.cle, quantity: 0 }]);
    }
  } catch (erreur) {
    console.warn('[kitomira] emballage : envoi classique', erreur);
    form.submit();
  } finally {
    caseACocher.disabled = false;
  }
});

// ---------- 3. Cadeau offert automatique ----------
// Le cadeau (et l'emballage) ne doit jamais rester payant : la réconciliation tourne au chargement de chaque page,
// au retour arrière (pageshow), à l'ouverture du tiroir panier et après chaque modification du panier.
// Shopify.actions arrive de façon asynchrone : on l'attend (tentatives bornées), sinon on passe par /cart/change.js et /cart/add.js.
let reconciliationEnCours = false;
let relancer = false;
let echecs = 0;
const racinePanier = () => window.Theme?.routes?.cart_url || '/cart';

/** @param {number} delaiMax */
function attendreActions(delaiMax) {
  return new Promise((resolve) => {
    const debut = Date.now();
    const tester = () => {
      if (window.Shopify?.actions?.updateCart) resolve(true);
      else if (Date.now() - debut >= delaiMax) resolve(false);
      else setTimeout(tester, 150);
    };
    tester();
  });
}

/** Repli sans Shopify.actions. @param {Array<{id?:string, merchandiseId?:string, quantity:number}>} lines */
async function modifierPanierRepli(lines) {
  const options = { method: 'POST', credentials: /** @type {RequestCredentials} */ ('same-origin'), headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  for (const ligne of lines) {
    let reponse;
    if (ligne.id) {
      reponse = await fetch(racinePanier() + '/change.js', { ...options, body: JSON.stringify({ id: ligne.id, quantity: ligne.quantity }) });
    } else if (ligne.merchandiseId) {
      const id = Number(String(ligne.merchandiseId).split('/').pop());
      reponse = await fetch(racinePanier() + '/add.js', { ...options, body: JSON.stringify({ items: [{ id, quantity: ligne.quantity }] }) });
    }
    if (reponse && !reponse.ok) throw new Error('panier : ' + reponse.status);
  }
}

function surPagePanier() {
  return //cart/?$/.test(location.pathname);
}
function tiroirOuvert() {
  return !!document.querySelector('cart-drawer-component dialog[open], cart-drawer-component [open]');
}

async function reconcilierPanier() {
  if (reconciliationEnCours) { relancer = true; return; }
  if (echecs >= 3) return;
  const cadeauConnu = CONFIG.cadeauProduit && CONFIG.cadeauVariante;
  const ajoutAuto = CONFIG.cadeauActif && cadeauConnu;
  if (!cadeauConnu && !CONFIG.emballageProduit) return;

  reconciliationEnCours = true;
  try {
    const reponse = await fetch(racinePanier() + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!reponse.ok) return;
    /** @type {{items: Array<{key:string, product_id:number, quantity:number, final_line_price:number}>}} */
    const panier = await reponse.json();

    let totalBijoux = 0;
    const lignesCadeau = [];
    const lignesEmballage = [];
    for (const ligne of panier.items) {
      if (cadeauConnu && ligne.product_id === CONFIG.cadeauProduit) lignesCadeau.push(ligne);
      else if (CONFIG.emballageProduit && ligne.product_id === CONFIG.emballageProduit) lignesEmballage.push(ligne);
      else totalBijoux += ligne.final_line_price;
    }

    /** @type {Array<{id?:string, merchandiseId?:string, quantity:number}>} */
    const lines = [];
    const qteCadeau = lignesCadeau.reduce((s, l) => s + l.quantity, 0);
    const droitAuCadeau = cadeauConnu && totalBijoux >= CONFIG.seuilCadeau;

    if (droitAuCadeau) {
      if (qteCadeau === 0 && ajoutAuto) lines.push({ merchandiseId: String(CONFIG.cadeauVariante), quantity: 1 });
      else if (qteCadeau > 1 || lignesCadeau.length > 1) {
        lignesCadeau.forEach((l, i) => lines.push({ id: l.key, quantity: i === 0 ? 1 : 0 }));
      }
    } else if (qteCadeau > 0) {
      // sous le palier (ou cadeau ajouté à la main) : le cadeau ne reste jamais au panier, donc jamais payé
      lignesCadeau.forEach((l) => lines.push({ id: l.key, quantity: 0 }));
    }
    // Emballage : une seule fois par commande
    if (lignesEmballage.reduce((s, l) => s + l.quantity, 0) > 1) {
      lignesEmballage.forEach((l, i) => lines.push({ id: l.key, quantity: i === 0 ? 1 : 0 }));
    }
    // Panier sans aucun bijou : l'emballage seul n'a pas de sens
    if (totalBijoux === 0 && panier.items.length === lignesEmballage.length + lignesCadeau.length && lignesEmballage.length) {
      lignesEmballage.forEach((l) => { if (!lines.some((x) => x.id === l.key)) lines.push({ id: l.key, quantity: 0 }); });
    }

    if (lines.length) {
      // un retrait (cadeau devenu payant) ne doit pas attendre : 400 ms au plus ; un ajout peut attendre 2 s
      const queDesRetraits = lines.every((l) => l.id && l.quantity === 0);
      if (await attendreActions(queDesRetraits ? 400 : 2000)) {
        await modifierPanier(lines); // met à jour le tiroir, la page panier et la pastille
      } else {
        await modifierPanierRepli(lines);
        // sans Shopify.actions, le panier affiché est obsolète : on le recharge s'il est visible
        if (surPagePanier() || tiroirOuvert()) location.reload();
      }
      echecs = 0;
    }
  } catch (erreur) {
    echecs += 1;
    console.warn('[kitomira] mise à jour automatique du panier impossible', erreur);
  } finally {
    reconciliationEnCours = false;
    if (relancer) { relancer = false; setTimeout(reconcilierPanier, 0); }
  }
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  const promesse = /** @type {any} */ (event).promise;
  if (promesse?.then) {
    promesse.then(() => setTimeout(reconcilierPanier, 0)).catch(() => {});
  } else {
    setTimeout(reconcilierPanier, 300);
  }
});
// ouverture du tiroir panier
document.addEventListener('theme-drawer:open', (event) => {
  const cible = /** @type {Element|null} */ (event.target instanceof Element ? event.target : null);
  if (!cible || cible.closest('cart-drawer-component') || cible.querySelector?.('cart-drawer-component')) reconcilierPanier();
});
// retour arrière depuis le paiement ou une autre page (cache navigateur)
window.addEventListener('pageshow', (event) => { if (event.persisted) reconcilierPanier(); });

// ---------- 4. Vidéos du héros ----------
// Les <video> n'ont pas de src dans le HTML (data-src) : rien n'est téléchargé avant la fin du chargement de la page,
// et seule la vidéo affichée (ordinateur OU mobile) est chargée puis lue (autoplay, muette, en boucle, playsinline).
// poster = l'image déjà affichée par <picture> (currentSrc, en cache), sinon data-poster (WebP).
// Lecture refusée (play() rejeté, ou rien après 4 s : économie d'énergie iOS, économiseur de données) :
// l'affiche bouge très lentement (Ken Burns, CSS) et un petit bouton permet de lancer la vidéo d'un geste.
function videoAffichee(video) {
  return getComputedStyle(video).display !== 'none' && video.getClientRects().length > 0;
}
function initialiserVideos() {
  const videos = [...document.querySelectorAll('video[data-kito-video]')].filter(videoAffichee);
  if (!videos.length) return; // vidéo du héros lue même avec « réduire les animations » (muette, bouton pause unique) : choix de Wyclef
  const economie = /** @type {any} */ (navigator).connection?.saveData === true;
  for (const video of videos) {
    const media = video.closest('.kito-heros__media');
    const bouton = media?.querySelector('[data-kito-heros-lecture]');
    const img = media?.querySelector('img.kito-heros__img');
    let refusee = false;
    let lue = false;
    const repli = () => {
      if (lue || refusee) return;
      refusee = true;
      media?.classList.add('kito-heros--ken-burns');
      if (bouton) bouton.hidden = false;
    };
    const charger = () => {
      if (video.src || !video.dataset.src) return;
      video.poster = (img && img.currentSrc) || video.dataset.poster || '';
      video.muted = true;
      video.playsInline = true;
      // pas d'attribut autoplay : la lecture passe toujours par play(), pour détecter un refus et respecter la pause mémorisée
      video.autoplay = false;
      video.preload = 'auto';
      video.src = video.dataset.src;
      video.addEventListener('playing', () => {
        lue = true;
        video.classList.add('kito-video--prete');
        media?.classList.remove('kito-heros--ken-burns');
        if (bouton) bouton.hidden = true;
      });
      // qualité déduite introuvable : retour à la source exposée par Shopify
      video.addEventListener('error', () => {
        const secours = video.dataset.srcRepli;
        if (secours && video.src.indexOf(secours.replace(/^https?:/, '')) === -1) { video.src = secours; lancer(); }
      }, { once: true });
    };
    const lancer = () => {
      if (animationsEnPause()) return;
      const promesse = video.play();
      if (promesse && promesse.catch) promesse.catch(repli);
      window.setTimeout(() => { if (!lue && video.paused) repli(); }, 4000);
    };
    bouton?.addEventListener('click', () => {
      charger();
      video.play().catch(() => {});
    });
    if (economie) { repli(); continue; }
    new IntersectionObserver((entrees) => {
      for (const entree of entrees) {
        if (entree.isIntersecting) { charger(); if (!refusee) lancer(); }
        else if (video.src) video.pause();
      }
    }).observe(video);
    document.addEventListener('kito:pause-animations', (e) => {
      const pause = /** @type {CustomEvent} */ (e).detail;
      if (pause) video.pause();
      else if (video.src && !refusee) lancer();
    });
  }
  reduireAnimations.addEventListener?.('change', () => {
  });
}

// ---------- 5. Bijou qui tourne : séquence d'images pilotée par le défilement ----------
function initialiserSequences() {
  if (reduireAnimations.matches) return; // affiche seule
  for (const vitrine of document.querySelectorAll("[data-kito-sequence]")) {
    const canvas = vitrine.querySelector("canvas");
    if (!(canvas instanceof HTMLCanvasElement)) continue;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;

    const mobile = window.matchMedia("(max-width: 749px)").matches;
    const base = (mobile ? vitrine.dataset.mobileBase : vitrine.dataset.ordiBase) || "";
    const nombre = Number(mobile ? vitrine.dataset.mobileNombre : vitrine.dataset.ordiNombre) || 0;
    if (!base.includes("001.") || nombre < 2) continue;
    const url = (i) => base.replace("001.", String(i + 1).padStart(3, "0") + ".");

    /** @type {(HTMLImageElement|null)[]} */
    const images = new Array(nombre).fill(null);
    let charge = false;
    let courant = -1;
    let demande = 0;

    const dimensionner = () => {
      const r = vitrine.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      courant = -1;
    };
    const dessiner = (i) => {
      // image chargée la plus proche
      let k = i, ecart = 0;
      while (ecart < nombre && !(images[k] && images[k].complete && images[k].naturalWidth)) {
        ecart += 1;
        k = (i + (ecart % 2 ? Math.ceil(ecart / 2) : -ecart / 2) + nombre) % nombre;
      }
      const img = images[k];
      if (!img || !img.naturalWidth || k === courant) return;
      courant = k;
      // object-fit: cover
      const cw = canvas.width, ch = canvas.height;
      const echelle = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * echelle, h = img.naturalHeight * echelle;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      vitrine.classList.add("kito-seq--pret");
    };
    const indexDefilement = () => {
      const r = vitrine.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
      return Math.min(nombre - 1, Math.floor(p * nombre));
    };
    const surDefilement = () => {
      if (demande) return;
      demande = requestAnimationFrame(() => { demande = 0; dessiner(indexDefilement()); });
    };
    const charger = () => {
      if (charge) return;
      charge = true;
      dimensionner();
      // la première image tout de suite, puis le reste dans l'ordre
      images.forEach((_, i) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => { if (Math.abs(i - indexDefilement()) <= 1 || courant < 0) surDefilement(); };
        img.src = url(i);
        images[i] = img;
      });
      // Horizon fait défiler .page-wrapper (et non la fenêtre) à partir de 990 px : on écoute tout défilement en phase de capture.
      document.addEventListener("scroll", surDefilement, { capture: true, passive: true });
      window.addEventListener("resize", () => { dimensionner(); surDefilement(); }, { passive: true });
    };
    new IntersectionObserver((entrees, obs) => {
      if (entrees.some((e) => e.isIntersecting)) { obs.disconnect(); charger(); }
    }, { rootMargin: "600px 0px" }).observe(vitrine);
  }
}

// ---------- 6. Flèches des carrousels ----------
function initialiserFleches() {
  for (const bloc of document.querySelectorAll("[data-kito-fleches]")) {
    const liste = document.getElementById(bloc.getAttribute("data-kito-fleches") || "");
    if (!liste) continue;
    const boutons = [...bloc.querySelectorAll("button[data-sens]")];
    const maj = () => {
      const max = liste.scrollWidth - liste.clientWidth - 2;
      boutons.forEach((b) => { b.disabled = Number(b.dataset.sens) < 0 ? liste.scrollLeft <= 2 : liste.scrollLeft >= max; });
    };
    boutons.forEach((b) => b.addEventListener("click", () => {
      const carte = liste.firstElementChild;
      const pas = carte ? carte.getBoundingClientRect().width + 12 : liste.clientWidth * 0.8;
      liste.scrollBy({ left: Number(b.dataset.sens) * pas, behavior: reduireAnimations.matches ? "auto" : "smooth" });
    }));
    liste.addEventListener("scroll", maj, { passive: true });
    window.addEventListener("resize", maj, { passive: true });
    bloc.hidden = false;
    maj();
  }
}

// ---------- 7. Motion sobre : apparitions au défilement (une fois) et lumière ambiante à l'écran seulement ----------
const SELECTEURS_APPARITION = [
  '.kito-tete',
  '.kito-familles',
  '.kito-seq__vitrine',
  '.kito-seq__texte',
  '.kito-categories > li',
  '.kito-lots__img',
  '.kito-lots__texte',
  '.kito-cadeaux > *',
  '.kito-confiance > li',
  '.kito-texte-col',
  '.product-grid__item',
  '.kito-pastilles',
].join(',');

function initialiserApparitions() {
  if (reduireAnimations.matches || !('IntersectionObserver' in window)) return;
  // Exclus : le héros (LCP), les fenêtres, et tout ce qui est dans un défilement horizontal (carrousels) :
  // une carte hors champ à droite ne croiserait jamais l'écran avant le geste de la cliente.
  const dansDefilementHorizontal = (el) => {
    for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
      // .page-wrapper d'Horizon défile (≥ 990 px) mais pas à l'horizontale : seul un vrai défilement horizontal compte.
      if (/(auto|scroll)/.test(getComputedStyle(a).overflowX) && a.scrollWidth > a.clientWidth + 1) return true;
    }
    return false;
  };
  const elements = [...document.querySelectorAll(SELECTEURS_APPARITION)].filter(
    (el) => !el.closest('.kito-heros, dialog, [data-kito-sans-apparition]') && !dansDefilementHorizontal(el)
  );
  if (!elements.length) return;
  const hauteur = window.innerHeight;
  const observateur = new IntersectionObserver(
    (entrees) => {
      for (const entree of entrees) {
        if (!entree.isIntersecting) continue;
        entree.target.classList.add('kito-vu');
        observateur.unobserve(entree.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  /** @type {Map<Element, number>} */
  const rangs = new Map();
  for (const el of elements) {
    el.setAttribute('data-kito-apparition', '');
    // Ce qui est déjà visible à l'arrivée ne s'anime pas (aucun effet sur l'affichage initial ni sur le LCP).
    if (el.getBoundingClientRect().top < hauteur * 0.92) {
      el.classList.add('kito-vu');
      continue;
    }
    // Léger décalage entre voisins (4 au plus), pour une cascade discrète.
    const parent = el.parentElement;
    const rang = parent ? rangs.get(parent) ?? 0 : 0;
    if (parent) rangs.set(parent, rang + 1);
    if (rang) /** @type {HTMLElement} */ (el).style.setProperty('--kito-delai', Math.min(rang, 4) * 80 + 'ms');
    observateur.observe(el);
  }
  document.documentElement.classList.add('kito-motion');
  // Filet de sécurité : si un élément n'est jamais signalé (onglet en arrière-plan, impression…), il apparaît quand même.
  window.addEventListener('beforeprint', () => elements.forEach((el) => el.classList.add('kito-vu')));
}

function initialiserLumiere() {
  if (reduireAnimations.matches || !('IntersectionObserver' in window)) return;
  const zones = document.querySelectorAll('.kito-heros__media, .kito-familles');
  const observateur = new IntersectionObserver((entrees) => {
    for (const entree of entrees) entree.target.classList.toggle('kito-lumiere--active', entree.isIntersecting);
  });
  zones.forEach((z) => observateur.observe(z));
  reduireAnimations.addEventListener?.('change', () => {
    if (reduireAnimations.matches) zones.forEach((z) => { observateur.unobserve(z); z.classList.remove('kito-lumiere--active'); });
  });
}

// ---------- 8. Vidéo des fiches produits ----------
// Horizon affiche l'affiche + un bouton lecture (deferred-media) : rien n'est téléchargé avant.
// Quand la vidéo est à l'écran (au moins 60 %), on la charge et on la lit muette, en boucle, en ligne (playsinline) ;
// hors écran, elle se met en pause. Jamais avec « réduire les animations » ni en mode économie de données.
function initialiserVideosProduit() {
  const economie = /** @type {any} */ (navigator).connection?.saveData === true;
  if (reduireAnimations.matches || economie || !('IntersectionObserver' in window)) return;
  /** @type {WeakSet<Element>} */
  const suivies = new WeakSet();
  const observateur = new IntersectionObserver(
    (entrees) => {
      for (const entree of entrees) {
        const media = /** @type {any} */ (entree.target);
        if (entree.isIntersecting && !animationsEnPause()) {
          if (!media.getAttribute('data-media-loaded')) {
            media.loadContent?.(false);
            const chargees = media.querySelectorAll('video');
            chargees[chargees.length - 1]?.addEventListener('playing', (e) => { /** @type {HTMLVideoElement} */ (e.target).dataset.kitoLue = '1'; }, { once: true });
            media.isPlaying = true;
            media.updatePlayPauseHint?.(true);
          } else if (!media.isPlaying) {
            media.playMedia?.();
          }
        } else if (media.getAttribute('data-media-loaded') && media.isPlaying) {
          // Une pause pendant le démarrage interromprait le play() d'Horizon (erreur dans la console) :
          // si la lecture n'a pas encore commencé, on attend « playing » puis on met en pause si la vidéo est toujours hors écran.
          const videos = media.querySelectorAll('video');
          const video = videos[videos.length - 1];
          if (video && video.paused && !video.dataset.kitoLue) {
            video.addEventListener('playing', () => {
              video.dataset.kitoLue = '1';
              const r = media.getBoundingClientRect();
              if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) media.pauseMedia?.();
            }, { once: true });
          } else {
            if (video) video.dataset.kitoLue = '1';
            media.pauseMedia?.();
          }
        }
      }
    },
    { threshold: 0.6 }
  );
  const balayer = () => {
    for (const media of document.querySelectorAll(
      'media-gallery[data-kito-video-auto="true"] .product-media-container--video deferred-media'
    )) {
      if (suivies.has(media) || media.closest('dialog')) continue;
      suivies.add(media);
      observateur.observe(media);
    }
  };
  customElements.whenDefined('deferred-media').then(() => {
    balayer();
    // Changement de coloris : Horizon remplace la galerie ; on suit les nouvelles vidéos.
    const zone = document.querySelector('.product-information') || document.body;
    let attente = 0;
    new MutationObserver(() => {
      clearTimeout(attente);
      attente = window.setTimeout(balayer, 200);
    }).observe(zone, { childList: true, subtree: true });
  });
}

// ---------- 9. Pause des animations (WCAG 2.2.2) : un seul bouton arrête tout ce qui bouge seul ----------
// Bandeau, respiration lumineuse, Ken Burns (CSS via html.kito-pause), vidéos (événement kito:pause-animations).
// Le choix est mémorisé (localStorage) et réappliqué avant l'affichage par un petit script de snippets/kito-styles.liquid.
const CLE_PAUSE = 'kito-animations-pause';
function animationsEnPause() {
  return document.documentElement.classList.contains('kito-pause');
}
function initialiserPauseAnimations() {
  const boutons = [...document.querySelectorAll('[data-kito-pause-animations]')];
  const appliquer = (pause, memoriser) => {
    document.documentElement.classList.toggle('kito-pause', pause);
    boutons.forEach((b) => { b.setAttribute('aria-pressed', String(pause)); const l = pause ? 'Reprendre les animations' : 'Mettre en pause les animations du site'; b.setAttribute('aria-label', l); b.title = l; });
    if (memoriser) { try { localStorage.setItem(CLE_PAUSE, pause ? '1' : '0'); } catch {} }
    document.dispatchEvent(new CustomEvent('kito:pause-animations', { detail: pause }));
  };
  let memo = false;
  try { memo = localStorage.getItem(CLE_PAUSE) === '1'; } catch {}
  appliquer(memo, false);
  for (const bouton of boutons) {
    bouton.hidden = false;
    bouton.addEventListener('click', () => appliquer(!animationsEnPause(), true));
  }
}

function demarrer() {
  initialiserPauseAnimations();
  initialiserApparitions();
  initialiserLumiere();
  initialiserSequences();
  initialiserFleches();
  reconcilierPanier();
  // vidéos : après le chargement complet de la page, pour ne pas concurrencer l'affiche et les images
  // (vidéo de fiche comprise : même sur ordinateur où elle est visible dès l'arrivée, elle passe après l'image principale)
  const videosDifferees = () => { initialiserVideos(); initialiserVideosProduit(); };
  const lancer = () => ("requestIdleCallback" in window ? requestIdleCallback(videosDifferees, { timeout: 2000 }) : setTimeout(videosDifferees, 200));
  if (document.readyState === "complete") lancer();
  else window.addEventListener("load", lancer, { once: true });
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", demarrer, { once: true });
} else {
  demarrer();
}

// ---------- Fiche : description repliée avec « Lire la suite » ----------
(() => {
  const desc = document.querySelector('.product-information rte-formatter[class*="__text_"]');
  if (!desc || desc.scrollHeight < 200) return;
  desc.classList.add('kito-desc-repliee');
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'kito-desc-suite'; b.textContent = 'Lire la suite'; b.setAttribute('aria-expanded', 'false');
  b.addEventListener('click', () => {
    const ouvert = desc.classList.toggle('kito-desc-repliee') === false;
    b.textContent = ouvert ? 'Réduire' : 'Lire la suite'; b.setAttribute('aria-expanded', String(ouvert));
  });
  desc.after(b);
})();
